import type { MetaSchemaField } from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";

/**
 * ProjectConfig — shape of `games.config` JSONB blob the admin edits.
 *
 * Mockup: project-config.html L447-L746. Backend stores this as opaque JSON;
 * the UI is the source of truth for the sub-keys and their defaults
 * (see ./defaults.ts).
 */
export interface ProjectConfig {
  chat: ChatConfig;
  guild: GuildConfig;
  help_request: HelpRequestConfig;
  rate_limits: RateLimitsConfig;
  profile: ProfileConfig;
  guild_assets: GuildAssetsConfig;
}

export interface ChatConfig {
  history_window: number;
  max_message_length: number;
  rate_limit_per_minute: number;
  /** Custom profanity words. Backend merges with its own default list. */
  profanity_words: string[];
}

export interface GuildConfig {
  max_members: number;
  require_unique_name: boolean;
  metadata_schema: SchemaField[];
}

export interface HelpRequestConfig {
  types: string[];
  default_ttl_seconds: number;
  max_active_per_player: number;
}

export interface RateLimitsConfig {
  auth_per_ip_per_minute: number;
  help_create_per_minute: number;
}

export interface ProfileConfig {
  stats_schema: SchemaField[];
  /**
   * Cosmetic fields in `players.meta`. Unlike `stats_schema`, an EMPTY
   * meta_schema is a strict refusal: the backend rejects every key.
   */
  meta_schema: MetaSchemaField[];
  allowed_avatars: string[];
}

export interface GuildAssetsConfig {
  allowed_logos: string[];
}

/**
 * Section keys — used for per-section dirty tracking, mod-dot rendering,
 * and the human title / sub-label rendered in each section header.
 */
export type SectionKey =
  | "chat"
  | "guild"
  | "help_request"
  | "rate_limits"
  | "profile"
  | "guild_assets";

export const SECTION_ORDER: SectionKey[] = [
  "chat",
  "guild",
  "help_request",
  "rate_limits",
  "profile",
  "guild_assets",
];

export const SECTION_META: Record<
  SectionKey,
  { title: string; apiName: string }
> = {
  chat: { title: "Chat & profanity", apiName: "games.config.chat" },
  guild: { title: "Guild defaults", apiName: "games.config.guild" },
  help_request: {
    title: "Help requests",
    apiName: "games.config.help_request",
  },
  rate_limits: { title: "Rate limits", apiName: "games.config.rate_limits" },
  profile: { title: "Profile", apiName: "games.config.profile" },
  guild_assets: {
    title: "Guild assets",
    apiName: "games.config.guild_assets",
  },
};
